import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  JaarrekeningBalans,
  JaarrekeningVenW
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  Subscription,
  switchMap, take,
  tap
} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type JaarrekeningVenWFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  kostensoort: FormControl<string | null>;
}>;

type JaarrekeningBalansFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  waardeOpening: FormControl<number | null>;
  waardeSlot: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tbs-regeling',
  templateUrl: './tbs-regeling.component.html',
  styleUrls: ['./tbs-regeling.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class TbsRegelingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public isPartner = false;
  public isDefAangifte = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      tap((data) => {
        this.isDefAangifte = data.taxType === 'DA';
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    tbsOpbrengsten: new FormArray<JaarrekeningVenWFormGroup>([]),
    tbsKosten: new FormArray<JaarrekeningVenWFormGroup>([]),
    tbsBezittingen: new FormArray<JaarrekeningBalansFormGroup>([]),
    tbsOnroerendeZaken: new FormArray<JaarrekeningBalansFormGroup>([]),
    tbsSchulden: new FormArray<JaarrekeningBalansFormGroup>([]),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(partner => {
        this.isPartner = partner;
        return this.incomeTaxEntity$.pipe(
          take(1),
          map(entity => ({ entity, partner })));
      })
    ).subscribe(({ entity, partner }) => {
      this.handleSwitch(entity, partner);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const toBedragen = (input: ReturnType<typeof this.extractInputForCalculation>) =>
      [...input.opbrengsten, ...input.kosten].map(row => row.bedrag ?? null);

    this.changeSubscriptions.add(
      merge(
        this.formGroup.controls.tbsOpbrengsten.valueChanges,
        this.formGroup.controls.tbsKosten.valueChanges
      ).pipe(
        map(() => this.saveForm()),
        map(() => this.extractInputForCalculation()),
        map(toBedragen),
        distinctUntilChanged((prev, curr) =>
          prev.length === curr.length && prev.every((v, i) => v === curr[i])
        ),
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((curr) => {
        if (curr.some(b => b !== null)) {
          this.dataService.calculateLatestIncomeTaxReturn().subscribe();
        }
      })
    );
  }

  private extractInputForCalculation(): {
    opbrengsten: { bedrag: number | null }[];
    kosten: { bedrag: number | null }[];
  } {
    return {
      opbrengsten: this.tbsOpbrengsten.getRawValue().map(item => ({
        bedrag: item.bedrag,
      })),
      kosten: this.tbsKosten.getRawValue().map(item => ({
        bedrag: item.bedrag,
      })),
    };
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    const data = details.box1?.resultaatUitOverigeWerkzaamheden;
    this.updateForm(data.tbsOpbrengsten || [], this.tbsOpbrengsten);
    this.updateForm(data.tbsKosten || [], this.tbsKosten);
    this.updateFormBalans(data.tbsBezittingen || [], this.tbsBezittingen);
    this.updateFormBalans(data.tbsOnroerendeZaken || [], this.tbsOnroerendeZaken);
    this.updateFormBalans(data.tbsSchulden || [], this.tbsSchulden);
  }

  get tbsOpbrengsten(): FormArray {
    return this.formGroup.get('tbsOpbrengsten') as FormArray;
  }

  get tbsKosten(): FormArray {
    return this.formGroup.get('tbsKosten') as FormArray;
  }

  get tbsBezittingen(): FormArray {
    return this.formGroup.get('tbsBezittingen') as FormArray;
  }

  get tbsOnroerendeZaken(): FormArray {
    return this.formGroup.get('tbsOnroerendeZaken') as FormArray;
  }

  get tbsSchulden(): FormArray {
    return this.formGroup.get('tbsSchulden') as FormArray;
  }

  addRijOpbrengsten(): void {
    this.tbsOpbrengsten.push(this.createFormGroup(), { emitEvent: false })
  }

  addRijKosten(): void {
    this.tbsKosten.push(this.createFormGroup(), { emitEvent: false })
  }

  verwijderRijOpbrengsten(index: number): void {
    this.tbsOpbrengsten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijKosten(index: number): void {
    this.tbsKosten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  addRijTbsBezittingen(): void {
    this.tbsBezittingen.push(this.createBalansFormGroup())
  }

  addRijTbsOnrZaken(): void {
    this.tbsOnroerendeZaken.push(this.createBalansFormGroup())
  }

  addRijTbsSchulden(): void {
    this.tbsSchulden.push(this.createBalansFormGroup())
  }

  verwijderRijTbsBezittingen(index: number): void {
    this.tbsBezittingen.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijTbsOnrZaken(index: number): void {
    this.tbsOnroerendeZaken.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijTbsSchulden(index: number): void {
    this.tbsSchulden.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: JaarrekeningVenW): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving?.trim() ?? null, [
        Validators.required,
      ]),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null, [
        Validators.required,
        Validators.min(0)
      ]),
      kostensoort: new FormControl<string | null>(item?.kostensoort ?? null),
    })
  }

  createBalansFormGroup(item?: JaarrekeningBalans): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving?.trim() ?? null),
      waardeOpening: new FormControl<number | null>(item?.waardeOpening ?? null),
      waardeSlot: new FormControl<number | null>(item?.waardeSlot ?? null),
    })
  }

  private updateForm(list: JaarrekeningVenW[], array: FormArray): void {
    const lijst = list || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  private updateFormBalans(list: JaarrekeningBalans[], array: FormArray): void {
    const lijst = list || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createBalansFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const rawValue = this.formGroup.getRawValue();

    const filterEmptyRows = (rows: { omschrijving: string | null, bedrag: number | null }[]) =>
      rows.filter(row => row.omschrijving?.trim() || row.bedrag !== null);

    const filterEmptyBalanceRows = (rows: { omschrijving: string | null, waardeOpening: number | null, waardeSlot: number | null }[]) =>
      rows.filter(row => row.omschrijving?.trim() || row.waardeOpening !== null || row.waardeSlot !== null);

    const update = (aangifte: Aangifte) => {
      const tbsOpbrengsten = filterEmptyRows(rawValue.tbsOpbrengsten);
      const tbsKosten = filterEmptyRows(rawValue.tbsKosten);
      const tbsBezittingen = filterEmptyBalanceRows(rawValue.tbsBezittingen);
      const tbsOnroerendeZaken = filterEmptyBalanceRows(rawValue.tbsOnroerendeZaken);
      const tbsSchulden = filterEmptyBalanceRows(rawValue.tbsSchulden);

      return {
        ...aangifte,
        box1: {
          ...aangifte.box1,
          resultaatUitOverigeWerkzaamheden: {
            ...aangifte.box1?.resultaatUitOverigeWerkzaamheden,
            tbsOpbrengsten: tbsOpbrengsten.length ? tbsOpbrengsten : undefined,
            tbsKosten: tbsKosten.length ? tbsKosten : undefined,
            tbsBezittingen: tbsBezittingen.length ? tbsBezittingen : undefined,
            tbsOnroerendeZaken: tbsOnroerendeZaken.length ? tbsOnroerendeZaken : undefined,
            tbsSchulden: tbsSchulden.length ? tbsSchulden : undefined,
          },
        },
      };
    };

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) return currentEntity;

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return { ...currentEntity, aangifte: updatedAangifte } as IncomeTaxEntity;
    });
  }
}
